import { Controller } from '@hotwired/stimulus';

// /prices universal search — "Showroom Palette" (design approved 2026-08-12,
// variant A of the 3-designer panel). A command-palette overlay mounted on
// /prices, /prices/:make and /prices/:make/:model.
//
// Data: ONE lazily-fetched JSON catalog (/prices/search-catalog) built from
// the same SeoPriceStat snapshot the pages render — all matching/ranking runs
// client-side, so keystrokes never hit the server (the pages are edge-cached
// and session-free; the palette must not undo that).
//
// Search logic ported from the vehisales-vue teardown where proven there
// (escape-then-highlight, year-token extraction, order-independent tokens)
// plus what vehisales lacks: VIN detection with the 17-cell register.
export default class extends Controller {
  static targets = ['trigger', 'scrim', 'input', 'results', 'count', 'scope', 'status'];
  static values = {
    catalogUrl: String,
    scopeMake: String,      // slug  ('' on /prices)
    scopeMakeName: String,  // display name
    scopeModel: String,     // slug  ('' outside model hubs)
    scopeModelName: String,
  };

  connect() {
    this.catalog = null;
    this.fetching = null;
    this.flat = [];
    this.sel = 0;
    // Scope is mutable: Backspace on an empty input pops model -> make -> all.
    this.searchScope = { make: this.scopeMakeValue || null, makeName: this.scopeMakeNameValue || '',
                   model: this.scopeModelValue || null, modelName: this.scopeModelNameValue || '' };
    this.boundHotkey = this.hotkey.bind(this);
    document.addEventListener('keydown', this.boundHotkey);
    // The sticky cluster bar (cluster-nav controller) opens the palette by
    // event — on most pages the bar is the only visible search entry.
    // The menu's Research box passes what the visitor typed (detail.query).
    this.boundOpenEvent = (e) => this.open(e && e.detail && e.detail.query);
    document.addEventListener('seo-search:open', this.boundOpenEvent);
    // Warm the catalog on intent (hover/focus) so the first open feels instant.
    this.boundWarm = () => this.ensureCatalog();
    if (this.hasTriggerTarget) {
      this.triggerTarget.addEventListener('mouseenter', this.boundWarm, { once: true });
      this.triggerTarget.addEventListener('focus', this.boundWarm, { once: true });
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHotkey);
    document.removeEventListener('seo-search:open', this.boundOpenEvent);
  }

  hotkey(e) {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this.open();
    }
  }

  ensureCatalog() {
    if (this.catalog) return Promise.resolve(this.catalog);
    if (!this.fetching) {
      this.fetching = fetch(this.catalogUrlValue, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => { this.catalog = data; return data; })
        .catch(() => { this.fetching = null; return null; });
    }
    return this.fetching;
  }

  // --- open / close ---------------------------------------------------------

  open(query) {
    if (typeof query === 'string' && query) this.inputTarget.value = query;
    this.scrimTarget.hidden = false;
    this.inputTarget.setAttribute('aria-expanded', 'true');
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.syncScopeChip();
    this.sel = 0;
    this.render();
    this.inputTarget.focus();
    this.ensureCatalog().then(() => { if (!this.scrimTarget.hidden) this.render(); });
  }

  close() {
    this.scrimTarget.hidden = true;
    this.inputTarget.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-expanded', 'false');
      // A bar_only page hides the trigger — focusing a hidden element drops
      // focus to <body>; leave it where the browser puts it instead.
      if (!this.triggerTarget.hidden) this.triggerTarget.focus();
    }
    this.inputTarget.value = '';
  }

  scrimClick(e) {
    if (e.target === this.scrimTarget) this.close();
  }

  // --- input ------------------------------------------------------------------

  onInput() {
    this.sel = 0;
    this.render();
  }

  onKeydown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); this.move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.move(-1); }
    else if (e.key === 'Enter') { e.preventDefault(); this.activate(); }
    else if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    else if (e.key === 'Backspace' && !this.inputTarget.value && (this.searchScope.model || this.searchScope.make)) {
      // Pop one scope level without a page reload — the hub's palette must
      // not trap the visitor inside the hub.
      if (this.searchScope.model) { this.searchScope.model = null; this.searchScope.modelName = ''; }
      else { this.searchScope.make = null; this.searchScope.makeName = ''; }
      this.syncScopeChip();
      this.render();
    }
  }

  rowsHover(e) {
    const row = e.target.closest('[data-idx]');
    if (row && Number(row.dataset.idx) !== this.sel) {
      this.sel = Number(row.dataset.idx);
      this.paintSelection(false);
    }
  }

  rowsClick(e) {
    const chip = e.target.closest('[data-fill]');
    if (chip) {
      this.inputTarget.value = chip.dataset.fill;
      this.sel = 0;
      this.render();
      this.inputTarget.focus();
      return;
    }
    const row = e.target.closest('[data-idx]');
    if (row) { this.sel = Number(row.dataset.idx); this.activate(); }
  }

  // Thumbnail fallback chain: scraped listing photo -> brand badge PNG ->
  // monogram. Bound PROGRAMMATICALLY right after every render — a Stimulus
  // `error->` action binds through a MutationObserver tick, and a local 404
  // fires `error` before that tick, leaving dead thumbs (seen live 2026-08-12).
  // `img.complete` catches an image that already failed before binding.
  bindThumbErrors() {
    this.resultsTarget.querySelectorAll('[data-thumb] img').forEach((img) => {
      img.onerror = () => this.thumbFallback(img);
      if (img.complete && img.naturalWidth === 0) this.thumbFallback(img);
    });
  }

  thumbFallback(img) {
    const wrap = img.closest('[data-thumb]');
    if (!wrap) return;
    if (img.dataset.badge && !img.src.endsWith(img.dataset.badge)) {
      img.src = img.dataset.badge;
      img.classList.add('seo-srch-thumb__img--badge');
      return;
    }
    wrap.innerHTML = `<span class="seo-srch-badge">${this.esc(wrap.dataset.mono || 'VA')}</span>`;
  }

  // --- search ----------------------------------------------------------------

  // Uppercase + strip non-alphanumerics before comparing, so "f150", "f-150"
  // and "F 150" all meet — the vehisales tokenization, minus SQL.
  norm(s) { return String(s).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  // Escape FIRST, then wrap matches — model/make strings are scraped data
  // (vehisales audit NAV-03: v-html on raw scraped text was a live XSS).
  mark(text, q) {
    const safe = this.esc(text);
    if (!q) return safe;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return safe;
    return this.esc(text.slice(0, i)) + '<mark>' + this.esc(text.slice(i, i + q.length)) + '</mark>'
      + this.esc(text.slice(i + q.length));
  }

  money(n) { return n == null ? '' : '$' + Math.round(n).toLocaleString('en-US'); }
  num(n) { return n == null ? '' : Number(n).toLocaleString('en-US'); }

  // A VIN is one unbroken 17-char letters+digits token (no I/O/Q). Detect a
  // partial as soon as the shape is unambiguous, but never shadow a real
  // make/model name ("silverado" is 9 chars of pure letters — digit gate).
  vinShape(raw) {
    const s = raw.trim().toUpperCase();
    if (/\s/.test(s)) return null;
    if (s.length < 8 || s.length > 17) return null;
    if (!/^[A-Z0-9]+$/.test(s)) return null;
    if ((s.match(/[0-9]/g) || []).length < 2) return null;
    if ((s.match(/[A-Z]/g) || []).length < 2) return null;
    const bad = [];
    for (let i = 0; i < s.length; i += 1) if ('IOQ'.includes(s[i])) bad.push(i);
    return { s, len: s.length, bad, complete: s.length === 17 && bad.length === 0 };
  }

  parse(raw) {
    const tokens = raw.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let year = null;
    const rest = [];
    const maxYear = new Date().getFullYear() + 2;
    tokens.forEach((t) => {
      const y = /^(19|20)\d{2}$/.test(t) ? Number(t) : null;
      if (y && y >= 1980 && y <= maxYear && !year) year = y;
      else rest.push(t);
    });
    let state = null;
    const kept = [];
    rest.forEach((t) => {
      const hit = !state && t.length >= 3 && (this.catalog?.states || [])
        .find((st) => st.s.startsWith(t) || this.norm(st.n).startsWith(this.norm(t)));
      if (hit) state = hit; else kept.push(t);
    });
    const wantsDep = kept.some((t) => t.length >= 3 && ('depreciation'.startsWith(t) || 'resale'.startsWith(t)));
    const text = kept.filter((t) => !(t.length >= 3 && ('depreciation'.startsWith(t) || 'resale'.startsWith(t)))).join(' ');
    return { year, state, text, wantsDep };
  }

  scoreName(name, q) {
    const n = this.norm(name);
    const w = this.norm(q);
    if (!w) return 0;
    if (n === w) return 120;
    if (n.startsWith(w)) return 100 - n.length * 0.1;
    const wordStart = String(name).toLowerCase().split(/[\s-]/).some((part) => this.norm(part).startsWith(w));
    if (wordStart) return 70;
    if (n.includes(w)) return 40;
    return 0;
  }

  yearEntry(model, year) { return (model.y || []).find((r) => r[0] === year); }

  build(raw) {
    const groups = [];
    const p = this.parse(raw);
    const vin = this.vinShape(raw);
    if (vin) groups.push({ label: 'VIN', rows: [{ kind: 'vin', vin }] });
    if (!this.catalog) return { groups, parsed: p, vin, loading: raw.trim().length >= 2 && !vin };

    const q = p.text;
    const scopedEmpty = !raw.trim() && (this.searchScope.make || this.searchScope.model);
    if (q.length < 2 && !p.year && !p.state && !p.wantsDep && !scopedEmpty) return { groups, parsed: p, vin };

    // --- model matches drive most groups -----------------------------------
    let modelHits = [];
    if (q.length >= 2) {
      this.catalog.models.forEach((m) => {
        const s = Math.max(
          this.scoreName(m.n, q),
          this.scoreName(`${this.makeName(m.mk)} ${m.n}`, q) - 5,
          this.scoreName(m.s, q) - 2,
        );
        if (s > 0) {
          const scopeBoost = (this.searchScope.make && m.mk === this.searchScope.make) ? 18 : 0;
          modelHits.push({ m, s: s + scopeBoost });
        }
      });
    } else if (this.searchScope.model) {
      const m = this.catalog.models.find((x) => x.mk === this.searchScope.make && x.s === this.searchScope.model);
      if (m) modelHits.push({ m, s: 100 });
    } else if (this.searchScope.make) {
      this.catalog.models.filter((m) => m.mk === this.searchScope.make)
        .forEach((m) => modelHits.push({ m, s: 50 }));
    } else if (p.year || p.state || p.wantsDep) {
      this.catalog.models.slice(0, 4).forEach((m) => modelHits.push({ m, s: 30 }));
    }
    modelHits.sort((a, b) => b.s - a.s || b.m.l - a.m.l);
    modelHits = modelHits.slice(0, 24);

    let makeHits = [];
    if (q.length >= 2) {
      this.catalog.makes.forEach((mk) => {
        const s = Math.max(this.scoreName(mk.n, q), this.scoreName(mk.s, q));
        if (s > 0) makeHits.push({ mk, s });
      });
    } else if (scopedEmpty && this.searchScope.make && !this.searchScope.model) {
      const mk = this.catalog.makes.find((x) => x.s === this.searchScope.make);
      if (mk) makeHits.push({ mk, s: 10 });
    }
    makeHits.sort((a, b) => b.s - a.s || b.mk.l - a.mk.l);

    const G = {};

    // YEAR PAGES
    const yearRows = [];
    const pushYear = (m, y) => {
      const yr = this.yearEntry(m, y);
      if (!yr) return;
      yearRows.push({
        kind: 'year', q,
        title: `${y} ${this.makeName(m.mk)} ${m.n}`,
        model: m, segs: ['prices', m.mk, m.s, String(y)], hits: [3],
        meta: 'year price page', price: this.money(yr[1]), sub: `${this.num(yr[2])} listings`,
      });
    };
    if (p.year && modelHits.length) {
      modelHits.slice(0, 4).forEach((h) => pushYear(h.m, p.year));
      if (yearRows.length === 1) {
        [p.year + 1, p.year - 1].forEach((y) => pushYear(modelHits[0].m, y));
      }
    } else if (modelHits.length && (q.length >= 2 || scopedEmpty) && !p.state) {
      (modelHits[0].m.y || []).slice(0, 3).forEach((r) => pushYear(modelHits[0].m, r[0]));
    }
    if (yearRows.length) G.year = { label: 'Year pages', rows: yearRows };

    // MODEL HUBS
    const modelRows = modelHits.slice(0, scopedEmpty && !this.searchScope.model ? 8 : 4).map((h) => {
      const m = h.m;
      const years = m.y || [];
      return {
        kind: 'model', q,
        title: `${this.makeName(m.mk)} ${m.n}`,
        model: m, segs: ['prices', m.mk, m.s], hits: [2],
        meta: `model hub · ${years.length} model year${years.length === 1 ? '' : 's'}`,
        price: this.money(m.p), sub: `${this.num(m.l)} listings`,
      };
    });
    if (modelRows.length) G.model = { label: 'Model hubs', rows: modelRows };

    // MAKE HUBS
    const makeRows = makeHits.slice(0, 3).map((h) => {
      const mk = h.mk;
      return {
        kind: 'make', q,
        title: mk.n, make: mk, segs: ['prices', mk.s], hits: [1],
        meta: `${mk.m} models · ${this.shortMoney(mk.pmin)}–${this.shortMoney(mk.pmax)}`,
        price: this.num(mk.l), sub: 'listings',
      };
    });
    if (makeRows.length) G.make = { label: 'Make hubs', rows: makeRows };

    // DEPRECIATION — only curves whose page actually renders (dep flag).
    const depRows = modelHits.filter((h) => h.m.dep).slice(0, p.wantsDep ? 3 : 1).map((h) => {
      const m = h.m;
      return {
        kind: 'dep', q,
        title: `${this.makeName(m.mk)} ${m.n} depreciation`,
        model: m, segs: ['depreciation', m.mk, m.s], hits: [1, 2],
        meta: 'value curve by model year', price: '', sub: '',
      };
    });
    if (depRows.length) G.dep = { label: 'Depreciation', rows: depRows };

    // STATE MARKETS
    const stateRows = [];
    if (p.state) {
      stateRows.push({
        kind: 'state', q,
        title: `${p.state.n} used car market`, mono: p.state.n.slice(0, 2).toUpperCase(),
        segs: ['market', p.state.s], hits: [1],
        meta: 'state market page', price: '', sub: '',
      });
      if (modelHits.length && p.year) {
        const m = modelHits[0].m;
        if (this.yearEntry(m, p.year)) {
          stateRows.push({
            kind: 'state', q,
            title: `${p.year} ${this.makeName(m.mk)} ${m.n} in ${p.state.n}`,
            model: m, segs: ['prices', m.mk, m.s, String(p.year), p.state.s], hits: [4],
            meta: 'year price in state', price: '', sub: '',
          });
        }
      }
    }
    if (stateRows.length) G.state = { label: 'State markets', rows: stateRows };

    // TOOLS
    const tools = [
      { title: 'Value my car', segs: ['value-my-car'], meta: 'free instant valuation', mono: '$' },
      { title: 'Market reports', segs: ['reports'], meta: 'monthly price movers', mono: '≡' },
    ];
    const toolRows = tools
      .filter((t) => !q || this.scoreName(t.title, q) > 0 || q.length >= 3)
      .map((t) => ({ kind: 'tool', q, title: t.title, mono: t.mono, segs: t.segs, hits: [], meta: t.meta, price: '', sub: '' }));
    if (toolRows.length && (makeRows.length || modelRows.length || yearRows.length)) {
      G.tool = { label: 'Tools', rows: toolRows.slice(0, 2) };
    }

    let order;
    if (scopedEmpty && this.searchScope.model) order = ['year', 'dep', 'model', 'tool'];
    else if (scopedEmpty) order = ['model', 'make', 'tool'];
    else if (p.year) order = ['year', 'model', 'make', 'dep', 'state', 'tool'];
    else if (p.wantsDep) order = ['dep', 'model', 'year', 'make', 'state', 'tool'];
    else if (p.state) order = ['state', 'model', 'make', 'year', 'dep', 'tool'];
    else order = ['make', 'model', 'year', 'dep', 'state', 'tool'];
    order.forEach((k) => { if (G[k]) groups.push(G[k]); });

    return { groups, parsed: p, vin };
  }

  makeName(slug) {
    const mk = (this.catalog?.makes || []).find((m) => m.s === slug);
    return mk ? mk.n : slug;
  }

  shortMoney(n) {
    if (n == null) return '—';
    return n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${n}`;
  }

  // --- rendering ---------------------------------------------------------------

  thumbHTML(row) {
    // Make rows and tools: badge/monogram straight away (a make has no photo).
    if (row.make) {
      const mono = this.esc(row.make.n.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase());
      if (row.make.logo) {
        return `<span class="seo-srch-thumb seo-srch-thumb--badge" data-thumb data-mono="${mono}">`
          + `<img src="/images/make-logos-small/${this.esc(row.make.s)}.png" alt="" loading="lazy" decoding="async"`
          + ' class="seo-srch-thumb__img seo-srch-thumb__img--badge"></span>';
      }
      return `<span class="seo-srch-thumb seo-srch-thumb--badge" data-thumb><span class="seo-srch-badge">${mono}</span></span>`;
    }
    if (row.model) {
      const m = row.model;
      const mono = this.esc(this.makeName(m.mk).replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase());
      const mk = (this.catalog?.makes || []).find((x) => x.s === m.mk);
      // Small pack (240px, ~4KB): the palette thumb renders well under 102 CSS
      // px; the full-size /images/make-logos pack is the window-sticker PRINT
      // pack (up to 1920x1080) and stays off the site.
      const badge = mk && mk.logo ? `/images/make-logos-small/${m.mk}.png` : '';
      if (m.img) {
        return `<span class="seo-srch-thumb" data-thumb data-mono="${mono}">`
          + `<img src="${this.esc(m.img)}" alt="" loading="lazy" decoding="async" class="seo-srch-thumb__img"`
          + ` data-badge="${this.esc(badge)}"></span>`;
      }
      if (badge) {
        return `<span class="seo-srch-thumb seo-srch-thumb--badge" data-thumb data-mono="${mono}">`
          + `<img src="${badge}" alt="" loading="lazy" decoding="async" class="seo-srch-thumb__img seo-srch-thumb__img--badge"`
          + '></span>';
      }
      return `<span class="seo-srch-thumb seo-srch-thumb--badge" data-thumb><span class="seo-srch-badge">${mono}</span></span>`;
    }
    return `<span class="seo-srch-thumb seo-srch-thumb--badge" data-thumb><span class="seo-srch-badge">${this.esc(row.mono || 'VA')}</span></span>`;
  }

  routeHTML(segs, hits) {
    return `<span class="seo-srch-route">${segs.map((s, i) => (
      `<span class="seo-srch-route__seg${hits.includes(i) ? ' seo-srch-route__seg--hit' : ''}">/${this.esc(s)}</span>`
    )).join('')}</span>`;
  }

  rowHTML(row, idx) {
    if (row.kind === 'vin') return this.vinHTML(row.vin, idx);
    const selected = idx === this.sel;
    return `<div class="seo-srch-row" role="option" id="seo-srch-row-${idx}" data-idx="${idx}" aria-selected="${selected}">`
      + this.thumbHTML(row)
      + '<span class="seo-srch-row__main">'
      + `<span class="seo-srch-row__title">${this.mark(row.title, row.q)}</span>`
      + `<span class="seo-srch-row__route">${this.routeHTML(row.segs, row.hits)}`
      + (row.meta ? `<span class="seo-srch-row__meta"> · ${this.esc(row.meta)}</span>` : '')
      + '</span></span>'
      + '<span class="seo-srch-row__data">'
      + (row.price ? `<span class="seo-srch-row__price seo-shx-mono">${this.esc(row.price)}</span>` : '')
      + (row.sub ? `<span class="seo-srch-row__sub seo-shx-mono">${this.esc(row.sub)}</span>` : '')
      + '</span></div>';
  }

  // The signature element: a physical 17-cell register with WMI/YEAR/SERIAL
  // captions. Filled cells glow, the next cell carries the caret, an illegal
  // I/O/Q turns its own cell red, 17/17 turns the register green.
  vinHTML(vin, idx) {
    const selected = idx === this.sel;
    let cells = '';
    for (let i = 0; i < 17; i += 1) {
      const ch = vin.s[i];
      const cls = ['seo-srch-vin__cell'];
      if (ch) cls.push(vin.bad.includes(i) ? 'seo-srch-vin__cell--err' : 'seo-srch-vin__cell--on');
      if (!ch && i === vin.len) cls.push('seo-srch-vin__cell--next');
      if (i === 2 || i === 9) cls.push('seo-srch-vin__cell--gap');
      cells += `<span class="${cls.join(' ')}">${ch ? this.esc(ch) : '·'}</span>`;
    }
    let state; let stateCls = ''; let sub;
    if (vin.bad.length) {
      state = 'invalid character'; stateCls = 'seo-srch-vin__state--bad';
      sub = `A VIN never contains I, O or Q — remove ${vin.bad.map((i) => vin.s[i]).join(', ')} to continue.`;
    } else if (vin.complete) {
      state = 'ready'; stateCls = 'seo-srch-vin__state--ok';
      sub = 'Opens the full vehicle record: decode, market position and listing history.';
    } else {
      state = `${17 - vin.len} left`;
      sub = 'Keep typing — VINs are exactly 17 characters.';
    }
    return `<div class="seo-srch-row seo-srch-vinrow" role="option" id="seo-srch-row-${idx}" data-idx="${idx}" aria-selected="${selected}">`
      + '<span class="seo-srch-vin__head">'
      + '<span class="seo-srch-row__title">Decode VIN</span>'
      + `<span class="seo-srch-vin__state ${stateCls}">${this.esc(state)}</span>`
      + `<span class="seo-srch-row__route"><span class="seo-srch-route">`
      + `<span class="seo-srch-route__seg">/vehicle</span><span class="seo-srch-route__seg seo-srch-route__seg--hit">/${this.esc(vin.s)}</span>`
      + '</span></span></span>'
      + `<span class="seo-srch-vin__reg" aria-hidden="true">${cells}</span>`
      + '<span class="seo-srch-vin__legend"><span>1–3 WMI</span><span>4–9 vehicle</span><span>10 year</span><span>11–17 serial</span></span>'
      + `<span class="seo-srch-vin__decode">${this.esc(sub)}</span>`
      + '</div>';
  }

  emptyHTML() {
    const scoped = this.searchScope.model
      ? `${this.searchScope.makeName} ${this.searchScope.modelName}`
      : (this.searchScope.make ? this.searchScope.makeName : null);
    const chips = this.searchScope.model
      ? [`2024 ${this.searchScope.modelName.toLowerCase()}`, `${this.searchScope.modelName.toLowerCase()} depreciation`, 'value my car']
      : (this.searchScope.make
        ? [`${this.searchScope.makeName.toLowerCase()} depreciation`, 'value my car']
        : ['chevrolet', 'ford', 'toyota', '2022 f-150', 'camry depreciation', 'value my car']);
    return '<div class="seo-srch-msg">'
      + `<h3>${scoped ? `Start inside ${this.esc(scoped)}` : 'Search the whole price index'}</h3>`
      + '<p>Type at least two characters. Add a year for a model-year page, a state for a local market, or paste a 17-character VIN.</p>'
      + `<div class="seo-srch-chips">${chips.map((c) => `<button type="button" class="seo-srch-chip" data-fill="${this.esc(c)}">${this.esc(c)}</button>`).join('')}</div>`
      + '</div>';
  }

  noResultsHTML(raw) {
    const chips = (this.catalog?.makes || []).slice(0, 6)
      .map((m) => `<button type="button" class="seo-srch-chip" data-fill="${this.esc(m.n.toLowerCase())}">${this.esc(m.n)}</button>`).join('');
    return '<div class="seo-srch-msg">'
      + `<h3>Nothing matches “${this.esc(raw.trim())}”</h3>`
      + '<p>Check the spelling, or start from a make and drill down from there.</p>'
      + `<div class="seo-srch-chips">${chips}</div>`
      + '</div>';
  }

  render() {
    const raw = this.inputTarget.value;
    const res = this.build(raw);
    this.flat = [];
    let html = '';

    if (res.loading) {
      html = '<div class="seo-srch-msg"><h3>Loading the index…</h3><p>One second — the catalog is on its way.</p></div>';
      this.countTarget.textContent = '';
    } else if (!res.groups.length && !raw.trim()) {
      html = this.emptyHTML();
      this.countTarget.textContent = '';
    } else if (!res.groups.length) {
      html = this.noResultsHTML(raw);
      this.countTarget.textContent = '0 results';
    } else {
      res.groups.forEach((g) => g.rows.forEach((r) => this.flat.push(r)));
      if (this.sel >= this.flat.length) this.sel = 0;
      let i = 0;
      html = res.groups.map((g) => {
        const rows = g.rows.map((r) => this.rowHTML(r, i++)).join('');
        return `<div class="seo-srch-group"><div class="seo-srch-eyebrow"><span>${this.esc(g.label)}</span>`
          + `<span class="seo-srch-eyebrow__ct">${g.rows.length}</span><span class="seo-srch-eyebrow__rule"></span></div>${rows}</div>`;
      }).join('');
      this.countTarget.textContent = res.vin
        ? `${res.vin.len}/17`
        : `${this.flat.length} result${this.flat.length === 1 ? '' : 's'}`;
    }

    this.resultsTarget.innerHTML = html;
    this.bindThumbErrors();
    this.paintSelection(true);
  }

  paintSelection(scroll) {
    const rows = this.resultsTarget.querySelectorAll('[data-idx]');
    rows.forEach((el) => {
      const on = Number(el.dataset.idx) === this.sel;
      el.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) {
        this.inputTarget.setAttribute('aria-activedescendant', el.id);
        if (scroll) el.scrollIntoView({ block: 'nearest' });
      }
    });
    if (!rows.length) this.inputTarget.setAttribute('aria-activedescendant', '');
  }

  move(d) {
    if (!this.flat.length) return;
    this.sel = (this.sel + d + this.flat.length) % this.flat.length;
    this.paintSelection(true);
  }

  activate() {
    const row = this.flat[this.sel];
    if (!row) return;
    if (row.kind === 'vin') {
      if (!row.vin.complete) {
        this.flashStatus(row.vin.bad.length
          ? 'Fix the VIN first — it never contains I, O or Q'
          : `${17 - row.vin.len} characters left`);
        return;
      }
      // /vehicle?vin= resolves to the canonical /vehicle/:slug (or the
      // vehileaks fallback) server-side — the same entry the homepage uses.
      window.location.assign(`/vehicle?vin=${encodeURIComponent(row.vin.s)}`);
      return;
    }
    window.location.assign(`/${row.segs.map(encodeURIComponent).join('/')}`);
  }

  flashStatus(msg) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = msg;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => { this.statusTarget.textContent = ''; }, 2600);
  }

  syncScopeChip() {
    if (!this.hasScopeTarget) return;
    const label = this.searchScope.model ? `${this.searchScope.make}/${this.searchScope.model}` : (this.searchScope.make || '');
    this.scopeTarget.textContent = label;
    this.scopeTarget.hidden = !label;
    this.inputTarget.placeholder = label ? `refine within ${label}` : 'make, model, year, state or VIN';
  }
}
